import Input from "components/form/input/Input";
import Sheet from "components/sheet/Sheet";
import React, { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import validator from "validator";
import { ButtonNew as Button } from "components/buttonNew";
import Toggle from "components/form/toggle/Toggle";
import "./NewCustomer.scss";

interface ICustomerProps {
  newCustomerSheetActive: boolean;
  onCancel?: () => void;
  onOk?: (customerInfo: ICustomerInfoState) => void;
  searchValue?: string;
}

interface IInputField {
  isDirty: boolean;
  isValid: boolean;
}

export interface ICustomerInfoState {
  firstName: string;
  lastName: string;
  emailAddress: string;
  phoneNumber: string;
  emailAddressValid: IInputField;
  sendWelcomeEmail: boolean;
  sendMarketingEmails?: boolean;
}

function NewCustomer(props: ICustomerProps) {
  const { t } = useTranslation();
  const [state, setState] = useState<ICustomerInfoState>({
    firstName: "",
    lastName: "",
    emailAddress: "",
    phoneNumber: "",
    emailAddressValid: { isDirty: false, isValid: false },
    sendWelcomeEmail: false,
  });

  useEffect(() => {
    if (props.newCustomerSheetActive && props.searchValue) {
      if (props.searchValue.includes("@")) {
        setState(prevState => ({
          ...prevState,
          emailAddress: props.searchValue,
          emailAddressValid: { isDirty: true, isValid: validator.isEmail(props.searchValue) },
        }));
      } else if (validator.isNumeric(props.searchValue)) {
        setState(prevState => ({ ...prevState, phoneNumber: props.searchValue }));
      } else if (props.searchValue.includes(" ")) {
        const fullName = props.searchValue.split(" ");
        if (validator.isAlpha(fullName[0])) {
          setState(prevState => ({ ...prevState, firstName: fullName[0] }));
        }
        if (validator.isAlpha(fullName[1])) {
          setState(prevState => ({ ...prevState, lastName: fullName[1] }));
        }
      } else if (validator.isAlpha(props.searchValue)) {
        setState(prevState => ({ ...prevState, firstName: props.searchValue }));
      }
    } else {
      setState(prevState => ({
        ...prevState,
        firstName: "",
        lastName: "",
        phoneNumber: "",
        emailAddress: "",
        emailAddressValid: { isDirty: false, isValid: false },
        sendWelcomeEmail: false,
      }));
    }
  }, [props.newCustomerSheetActive, props.searchValue]);

  function handleInputChange(e: React.ChangeEvent<HTMLInputElement>) {
    const { id, value } = e.target;
    setState(prevState => ({
      ...prevState,
      [id]: value,
      emailAddressValid: id === "emailAddress" ? setEmailValidState(value) : prevState.emailAddressValid,
    }));
  }

  function setEmailValidState(email: string) {
    return email === "" ? { isDirty: false, isValid: false } : { isDirty: true, isValid: validator.isEmail(email) };
  }

  function onOk() {
    void props.onOk({ ...state });
  }

  function onClear() {
    setState(prevState => ({
      ...prevState,
      firstName: "",
      lastName: "",
      phoneNumber: "",
      emailAddress: "",
      emailAddressValid: { isDirty: false, isValid: false },
      sendWelcomeEmail: false,
    }));
  }

  function onCancel() {
    onClear();
    props.onCancel();
  }

  function handleToggleChange(event: React.ChangeEvent<HTMLInputElement>) {
    const { id, checked } = event.target;
    setState(prevState => ({ ...prevState, [id]: checked }));
  }

  const okDisabled =
    state.firstName === "" ||
    state.lastName === "" ||
    (!state.emailAddressValid?.isValid && state.phoneNumber === "") ||
    (state.emailAddressValid?.isDirty && !state.emailAddressValid?.isValid && state.phoneNumber?.length > 0);

  return (
    <Sheet
      open={props.newCustomerSheetActive}
      size="small"
      closable
      stacked
      height="flexible"
      title={t("New customer")}
      onCancel={onCancel}
      footer={null}
      secondaryFooter={
        <div className="ui-sheet-footer" style={{ margin: "16px -24px 0", paddingTop: "24px" }}>
          <div className="ui-sheet-footer-action">
            <Button type="text" className="text-md text-semibold text-gray-900" onClick={onClear}>
              {t("Clear all")}
            </Button>
          </div>
          <div className="ui-sheet-footer-actions">
            <div className="ui-sheet-footer-action">
              <Button type="default" onClick={onCancel}>
                {t("Go back")}
              </Button>
            </div>
            <div className="ui-sheet-footer-action">
              <Button type="primary" onClick={onOk} disabled={okDisabled}>
                {t("Save customer")}
              </Button>
            </div>
          </div>
        </div>
      }
    >
      <div className="new-customer-fields">
        <Input
          label={t("First name")}
          placeholder={t("Enter first name...")}
          id="firstName"
          value={state.firstName}
          onChange={handleInputChange}
        />
        <Input
          label={t("Last name")}
          placeholder={t("Enter last name...")}
          id="lastName"
          value={state.lastName}
          onChange={handleInputChange}
        />
        <Input
          label={t("Email")}
          placeholder={t("Enter an email address...")}
          id="emailAddress"
          value={state.emailAddress}
          onChange={handleInputChange}
          error={state?.emailAddressValid?.isDirty && !state?.emailAddressValid?.isValid}
        />
        <Input
          label={t("Phone number")}
          placeholder={t("Enter phone number...")}
          id="phoneNumber"
          value={state.phoneNumber}
          onChange={handleInputChange}
        />
      </div>
      <Toggle
        id="sendWelcomeEmail"
        labelRight={t("Send welcome email")}
        labelSupport={t("A welcome email will be sent to the customer confirming their new account.")}
        onChange={handleToggleChange}
        checked={state.sendWelcomeEmail}
      />
    </Sheet>
  );
}

export default NewCustomer;
